import React, { useMemo, useState } from 'react';
import { Card, ListGroup, Badge, Form, Row, Col } from 'react-bootstrap';
import { Atendimento } from '../../../types';
import { Trophy, Medal, Award, User, Calendar, Filter } from 'lucide-react';

interface ServicesRankingProps {
   atendimentos: Atendimento[];
}

const ServicesRanking: React.FC<ServicesRankingProps> = ({ atendimentos }) => {
   const [periodo, setPeriodo] = useState<string>('mes');
   const [dataInicio, setDataInicio] = useState<string>('');
   const [dataFim, setDataFim] = useState<string>('');

   const ranking = useMemo(() => {
      const counts: Record<string, number> = {};

      // Data de hoje
      const hoje = new Date();
      const hojeStr = hoje.toISOString().split('T')[0];

      // Data da semana (7 dias atrás)
      const semanaPassada = new Date(hoje);
      semanaPassada.setDate(hoje.getDate() - 7);
      const semanaPassadaStr = semanaPassada.toISOString().split('T')[0];

      // Data do mês (primeiro dia do mês atual)
      const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const primeiroDiaMesStr = primeiroDiaMes.toISOString().split('T')[0];

      atendimentos.forEach(atendimento => {
         // Conta apenas atendimentos não cancelados
         if (atendimento.status === 'cancelado' || !atendimento.funcionarioNome) {
            return;
         }

         const dataAtend = atendimento.dataAtendimento;

         // Filtros de Data
         if (periodo === 'hoje') {
            if (dataAtend !== hojeStr) return;
         } else if (periodo === 'semana') {
            if (dataAtend < semanaPassadaStr) return;
         } else if (periodo === 'mes') {
            if (dataAtend < primeiroDiaMesStr) return;
         } else if (periodo === 'personalizado') {
            if (dataInicio && dataAtend < dataInicio) return;
            if (dataFim && dataAtend > dataFim) return;
         }

         const name = atendimento.funcionarioNome;
         counts[name] = (counts[name] || 0) + 1;
      });

      return Object.entries(counts)
         .sort(([, a], [, b]) => b - a)
         .map(([name, count], index) => ({
            name,
            count,
            position: index + 1
         }));
   }, [atendimentos, periodo, dataInicio, dataFim]);

   const getIcon = (position: number) => {
      switch (position) {
         case 1:
            return <Trophy size={22} className="text-warning" fill="#ffc107" fillOpacity={0.3} />;
         case 2:
            return <Medal size={22} className="text-secondary" fill="#6c757d" fillOpacity={0.3} />;
         case 3:
            return <Award size={22} className="text-danger" fill="#dc3545" fillOpacity={0.3} />;
         default:
            return <span className="fw-bold text-muted h6 mb-0">#{position}</span>;
      }
   };

   return (
      <Card className="border-0 shadow-sm h-100">
         <Card.Header className="filtros-container">
            <div className="d-flex align-items-center justify-content-between mb-2" >
               <span className="fw-bold text-uppercase" style={{ fontSize: '0.8rem' }}>Filtros</span>
               <Badge bg="light" text="dark" className="border">
                  {ranking.reduce((acc, curr) => acc + curr.count, 0)} serviços
               </Badge>
            </div>
            <div >
               <Row>
                  <Col md={periodo === 'personalizado' ? 3 : 3}>
                     <Form.Group>
                        <Form.Label>Período</Form.Label>
                        <Form.Select
                           value={periodo}
                           onChange={(e) => setPeriodo(e.target.value)}
                           className=" text-muted fw-semibold"
                           style={{ fontSize: '0.85rem' }}
                        >
                           <option value="hoje">Hoje</option>
                           <option value="semana">Últimos 7 dias</option>
                           <option value="mes">Este Mês</option>
                           <option value="personalizado">Personalizado</option>
                           <option value="todos">Todo o período</option>
                        </Form.Select>
                     </Form.Group>
                  </Col>
                  {periodo === 'personalizado' && (
                     <>
                        <Col md={3}>
                           <Form.Group>
                              <Form.Label>Data Início</Form.Label>
                              <Form.Control
                                 type="date"
                                 value={dataInicio}
                                 onChange={(e) => setDataInicio(e.target.value)}
                                 style={{ fontSize: '0.8rem' }}
                              />
                           </Form.Group>
                        </Col>
                        <Col md={3}>
                           <Form.Label>Data Fim</Form.Label>
                           <Form.Control
                              type="date"
                              value={dataFim}
                              onChange={(e) => setDataFim(e.target.value)}
                              style={{ fontSize: '0.8rem' }}
                           />
                        </Col>
                     </>

                  )}
               </Row>
            </div>

         </Card.Header>
         <Card.Body className="p-0" style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <ListGroup variant="flush">
               {ranking.length > 0 ? (
                  ranking.map((item, index) => (
                     <ListGroup.Item
                        key={item.name}
                        className="d-flex align-items-center justify-content-between py-3 px-3 border-bottom"
                        style={{
                           backgroundColor: index === 0 ? 'rgba(255, 193, 7, 0.05)' : 'transparent'
                        }}
                     >
                        <div className="d-flex align-items-center gap-3">
                           <div style={{ width: '30px', display: 'flex', justifyContent: 'center' }}>
                              {getIcon(item.position)}
                           </div>
                           <div className="d-flex align-items-center gap-2">
                              <div
                                 className="rounded-circle d-flex align-items-center justify-content-center"
                                 style={{
                                    width: '32px',
                                    height: '32px',
                                    backgroundColor: 'var(--bs-gray-100)',
                                    color: 'var(--bs-gray-600)'
                                 }}
                              >
                                 <User size={16} />
                              </div>
                              <div>
                                 <div className="fw-semibold text-dark" style={{ fontSize: '0.95rem' }}>
                                    {item.name}
                                 </div>
                                 {index === 0 && (
                                    <div className="text-warning small fw-bold" style={{ fontSize: '0.75rem' }}>
                                       Top Atendente! 🌟
                                    </div>
                                 )}
                              </div>
                           </div>
                        </div>
                        <div className="text-end">
                           <Badge bg={index < 3 ? 'primary' : 'light'} text={index < 3 ? 'white' : 'dark'} pill className="px-3">
                              {item.count}
                           </Badge>
                        </div>
                     </ListGroup.Item>
                  ))
               ) : (
                  <div className="text-center py-5 text-muted">
                     <Filter size={32} className="mb-2 opacity-25" />
                     <p className="mb-0 small">Nenhum atendimento neste período</p>
                  </div>
               )}
            </ListGroup>
         </Card.Body>
      </Card>
   );
};

export default ServicesRanking;